import {
	DEFAULT_PAGE_SIZE,
	DEFAULT_TABLE_SIZE,
	PAGE_SIZE_OPTIONS,
	TABLE_PAGINATION_POSITION,
} from "@di-devs/constants";
import type { TablePaginationConfig } from "antd";
import { useMemo } from "react";

/**
 * Create a table configuration object with optional customizations
 * Provides consistent pagination and display settings across the application
 *
 * @param options - Optional customizations for the table config
 * @param options.pageSize - Number of items per page (default: 20)
 * @param options.size - Table size, affects pagination size (default: "default")
 * @param options.position - Pagination position (default: ["bottomCenter"])
 * @param options.showTotal - Function to display total items (default: `全${total}件中${range[0]}～${range[1]}件目`)
 * @param options.showSizeChanger - Show page size selector (default: true)
 * @param options.showQuickJumper - Show quick page jumper (default: true)
 * @param options.current - Current page number (default: 1)
 * @param options.total - Total number of items (default: 0)
 * @param options.onChange - Page change handler
 * @param options.onShowSizeChange - Page size change handler
 * @returns Table configuration object compatible with Ant Design Table pagination
 */
export const createTableConfig = (
	options: TablePaginationConfig = {},
): TablePaginationConfig => {
	const {
		pageSize = DEFAULT_PAGE_SIZE,
		size = DEFAULT_TABLE_SIZE,
		position = TABLE_PAGINATION_POSITION as TablePaginationConfig["position"],
		showTotal = (total: number, range: [number, number]) =>
			`全${total}件中${range[0]}～${range[1]}件目`,
		showSizeChanger = true,
		showQuickJumper = true,
		current = 1,
		total = 0,
		onChange,
		onShowSizeChange,
	} = options;

	// Extract size for table, not pagination (pagination only supports "small" | "default")
	const paginationSize = size === "small" ? "small" : "default";

	return {
		showSizeChanger,
		showQuickJumper,
		showTotal,
		defaultPageSize: pageSize,
		pageSizeOptions: PAGE_SIZE_OPTIONS,
		position,
		current,
		total,
		pageSize,
		size: paginationSize,
		onChange,
		onShowSizeChange,
	};
};

/**
 * Hook for creating table configuration with optional customizations
 * Memoizes the configuration to prevent unnecessary re-renders
 *
 * @param options - Optional customizations for the table config (same as createTableConfig)
 * @param options.pageSize - Number of items per page (default: 20)
 * @param options.size - Table size, affects pagination size (default: "default")
 * @param options.position - Pagination position (default: ["bottomCenter"])
 * @param options.showTotal - Function to display total items (default: Japanese format)
 * @param options.showSizeChanger - Show page size selector (default: true)
 * @param options.showQuickJumper - Show quick page jumper (default: true)
 * @param options.current - Current page number (default: 1)
 * @param options.total - Total number of items (default: 0)
 * @param options.onChange - Page change handler
 * @param options.onShowSizeChange - Page size change handler
 * @returns Memoized table configuration object
 */
export const useTableConfig = (options: TablePaginationConfig = {}) => {
	return useMemo(() => createTableConfig(options), [options]);
};
